// src/modules/shared/hooks/api/messaging/whatsapp/integrations.ts

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemoizedArrayData } from 'hooks/api/api';
import type { ApiResponse } from 'hooks/api/api';
import {
  MessagingWhatsappIntegrationPolicyService,
  MessagingWhatsappIntegrationsService,
  MessagingWhatsappRecoveryService,
} from 'src/api/generated';
import type {
  WhatsAppIntegration,
  WhatsAppIntegrationPolicyResponse,
  WhatsAppIntegrationPolicyUpdateBody,
  WhatsAppIntegrationCreateBodyParams as WhatsAppIntegrationCreateParams,
  WhatsAppOnboardingConfigurationResponse,
} from 'src/api/generated';
import { useSupportTenant } from 'src/modules/shared/contexts/support-tenant-context';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { refreshWhatsAppAvailability } from 'src/modules/shared/utils/integrations/refresh-whatsapp-availability.util';

const doNothing = () => undefined;

/** Tenant-scoped React Query keys for WhatsApp integration state. */
export const whatsAppIntegrationQueryKeys = {
  integrations: (tenantId: string | null) =>
    ['messaging/whatsapp/integrations', tenantId] as const,
  phones: (tenantId: string | null) =>
    ['messaging/whatsapp/phones', tenantId] as const,
  policy: (tenantId: string | null) =>
    ['messaging/whatsapp/integration-policy', tenantId] as const,
};

const useEffectiveTenantId = (): string | null => {
  const { assumedTenantId } = useSupportTenant();
  const { user: currentUser } = useCurrentUser();
  return assumedTenantId ?? currentUser?.tenantId ?? null;
};

const requireTenantId = (
  tenantId: string | null,
  errorMessage: string
): string => {
  if (!tenantId) {
    throw new Error(errorMessage);
  }
  return tenantId;
};

interface UseWhatsAppIntegrationCreateResponse {
  /** Whether an integration creation request is in flight. */
  loading: boolean;
  /** The latest integration creation error, if any. */
  error: Error | null;
  /** The integration returned by the latest successful creation. */
  integration?: WhatsAppIntegration;
  /** Starts WhatsApp integration creation for the effective tenant. */
  createWhatsAppIntegration: (params: WhatsAppIntegrationCreateParams) => void;
}

interface UseWhatsAppIntegrationListResponse extends ApiResponse {
  /** WhatsApp integrations belonging to the effective tenant. */
  integrations: WhatsAppIntegration[];
}

/**
 * Creates a WhatsApp integration and refreshes tenant-scoped integration data.
 *
 * @param onSuccess - Callback invoked with the created integration.
 * @param onError - Callback invoked when integration creation fails.
 * @returns Mutation state and a function that starts integration creation.
 */
export const useCreateWhatsAppIntegration = (
  onSuccess: (integration: WhatsAppIntegration) => void = doNothing,
  onError: () => void = doNothing
): UseWhatsAppIntegrationCreateResponse => {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const effectiveTenantId = useEffectiveTenantId();

  const mutation = useMutation({
    mutationKey: ['messaging/whatsapp/integrations/create', effectiveTenantId],
    mutationFn: async ({
      params,
      tenantId,
    }: {
      params: WhatsAppIntegrationCreateParams;
      tenantId: string | null;
    }): Promise<WhatsAppIntegration> => {
      requireTenantId(
        tenantId,
        t('settings.integrations.whatsapp.errors.tenantUnavailable')
      );
      return await MessagingWhatsappIntegrationsService.createIntegration(
        params
      );
    },
    onSettled: (_result, _error, { tenantId }) => {
      refreshWhatsAppAvailability(queryClient, tenantId);
    },
    onSuccess: (integration) => onSuccess(integration),
    onError: () => onError(),
  });

  return {
    loading: mutation.isPending,
    error: mutation.error,
    integration: mutation.data,
    createWhatsAppIntegration: (params) =>
      mutation.mutate({ params, tenantId: effectiveTenantId }),
  };
};

/** Revalidates saved setup without obtaining another Meta authorization code. */
export const useRecoverWhatsAppIntegration = (
  onSuccess: () => void = doNothing,
  onError: () => void = doNothing
) => {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const effectiveTenantId = useEffectiveTenantId();
  const mutation = useMutation({
    mutationKey: [
      'messaging/whatsapp/integrations/recovery',
      effectiveTenantId,
    ],
    mutationFn: ({ id, tenantId }: { id: string; tenantId: string | null }) => {
      requireTenantId(
        tenantId,
        t('settings.integrations.whatsapp.errors.tenantUnavailable')
      );
      return MessagingWhatsappRecoveryService.recoverIntegration(id);
    },
    onSuccess,
    onError,
    onSettled: (_result, _error, { tenantId }) => {
      refreshWhatsAppAvailability(queryClient, tenantId);
    },
  });
  return {
    loading: mutation.isPending,
    recoverWhatsAppIntegration: (id: string) =>
      mutation.mutate({ id, tenantId: effectiveTenantId }),
  };
};

/**
 * Loads WhatsApp integrations for the effective tenant context.
 *
 * @param options.enabled - Whether the integrations query may run.
 * @returns Tenant-scoped integrations and query state.
 */
export const useWhatsAppIntegrations = ({
  enabled = true,
}: { enabled?: boolean } = {}): UseWhatsAppIntegrationListResponse => {
  const effectiveTenantId = useEffectiveTenantId();
  const fetchIntegrations = async (): Promise<WhatsAppIntegration[]> => {
    return await MessagingWhatsappIntegrationsService.listIntegrations();
  };

  const {
    data,
    error,
    isLoading: loading,
    refetch,
  } = useQuery({
    queryKey: whatsAppIntegrationQueryKeys.integrations(effectiveTenantId),
    queryFn: fetchIntegrations,
    enabled: enabled && effectiveTenantId !== null,
  });

  const integrations = useMemoizedArrayData<WhatsAppIntegration>(data);

  return {
    loading,
    error,
    integrations,
    refetch,
  };
};

interface UseWhatsAppIntegrationPolicyResponse {
  /** Whether the policy query is in flight. */
  loading: boolean;
  /** The latest policy query error, if any. */
  error: Error | null;
  /** The effective tenant WhatsApp onboarding policy. */
  policy?: WhatsAppIntegrationPolicyResponse;
  /** Reloads the effective tenant policy. */
  refetch: () => Promise<unknown>;
}

/**
 * Loads the WhatsApp onboarding policy only for authorized billing admins.
 *
 * @param options.enabled - Whether the caller is authorized to load the policy.
 * @returns Tenant-scoped policy data and query state.
 */
export const useWhatsAppIntegrationPolicy = ({
  enabled,
}: {
  enabled: boolean;
}): UseWhatsAppIntegrationPolicyResponse => {
  const effectiveTenantId = useEffectiveTenantId();
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: whatsAppIntegrationQueryKeys.policy(effectiveTenantId),
    queryFn: () =>
      MessagingWhatsappIntegrationPolicyService.getIntegrationPolicy(),
    enabled: enabled && effectiveTenantId !== null,
  });

  return {
    loading: enabled && isLoading,
    error,
    policy: data,
    refetch,
  };
};

/**
 * Updates the tenant policy with optimistic revision protection.
 *
 * @returns Mutation state and a function that persists a revised policy.
 */
export const useUpdateWhatsAppIntegrationPolicy = (): {
  /** Whether a policy update is in flight. */
  loading: boolean;
  /** The latest policy update error, if any. */
  error: Error | null;
  /** Persists a policy update for the effective tenant. */
  updateWhatsAppIntegrationPolicy: (
    body: WhatsAppIntegrationPolicyUpdateBody
  ) => Promise<WhatsAppIntegrationPolicyResponse>;
} => {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const effectiveTenantId = useEffectiveTenantId();
  const mutation = useMutation({
    mutationKey: [
      'messaging/whatsapp/integration-policy/update',
      effectiveTenantId,
    ],
    mutationFn: ({
      body,
      tenantId,
    }: {
      body: WhatsAppIntegrationPolicyUpdateBody;
      tenantId: string | null;
    }) => {
      requireTenantId(
        tenantId,
        t('settings.integrations.whatsapp.errors.tenantUnavailable')
      );
      return MessagingWhatsappIntegrationPolicyService.updateIntegrationPolicy(
        body
      );
    },
    onSuccess: (policy, { tenantId }) => {
      queryClient.setQueryData(
        whatsAppIntegrationQueryKeys.policy(tenantId),
        policy
      );
    },
  });

  return {
    loading: mutation.isPending,
    error: mutation.error,
    updateWhatsAppIntegrationPolicy: (body) =>
      mutation.mutateAsync({ body, tenantId: effectiveTenantId }),
  };
};

/**
 * Creates the durable billing snapshot before opening Embedded Signup.
 *
 * @returns Mutation state and a function that creates onboarding configuration.
 */
export const useCreateWhatsAppOnboardingConfiguration = (): {
  /** Whether onboarding configuration creation is in flight. */
  loading: boolean;
  /** The latest onboarding configuration error, if any. */
  error: Error | null;
  /** Creates an immutable onboarding attempt for the effective tenant. */
  createWhatsAppOnboardingConfiguration: () => Promise<WhatsAppOnboardingConfigurationResponse>;
} => {
  const { t } = useI18n();
  const effectiveTenantId = useEffectiveTenantId();
  const mutation = useMutation({
    mutationKey: [
      'messaging/whatsapp/onboarding-configuration/create',
      effectiveTenantId,
    ],
    mutationFn: (tenantId: string | null) => {
      requireTenantId(
        tenantId,
        t('settings.integrations.whatsapp.errors.tenantUnavailable')
      );
      return MessagingWhatsappIntegrationPolicyService.createOnboardingConfiguration();
    },
  });

  return {
    loading: mutation.isPending,
    error: mutation.error,
    createWhatsAppOnboardingConfiguration: () =>
      mutation.mutateAsync(effectiveTenantId),
  };
};

/**
 * Deletes an eligible WhatsApp integration and refreshes tenant-scoped data.
 *
 * @param onSuccess - Callback invoked after deletion succeeds.
 * @param onError - Callback invoked when deletion fails.
 * @returns Mutation state and a function that requests integration deletion.
 */
export const useDeleteWhatsAppIntegration = (
  onSuccess: () => void = doNothing,
  onError: () => void = doNothing
): {
  /** Whether an integration deletion is in flight. */
  loading: boolean;
  /** The latest integration deletion error, if any. */
  error: Error | null;
  /** Deletes the integration identified by its provider ID. */
  deleteWhatsAppIntegration: (integrationProviderId: string) => void;
} => {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const effectiveTenantId = useEffectiveTenantId();

  const mutation = useMutation({
    mutationKey: ['messaging/whatsapp/integrations/delete', effectiveTenantId],
    mutationFn: async ({
      integrationProviderId,
      tenantId,
    }: {
      integrationProviderId: string;
      tenantId: string | null;
    }): Promise<{ integrationProviderId: string; deletedPhones: number }> => {
      requireTenantId(
        tenantId,
        t('settings.integrations.whatsapp.errors.tenantUnavailable')
      );
      return await MessagingWhatsappIntegrationsService.deleteIntegration(
        integrationProviderId
      );
    },
    onSettled: (_result, _error, { tenantId }) => {
      refreshWhatsAppAvailability(queryClient, tenantId);
    },
    onSuccess: () => onSuccess(),
    onError: () => onError(),
  });

  return {
    loading: mutation.isPending,
    error: mutation.error,
    deleteWhatsAppIntegration: (integrationProviderId) =>
      mutation.mutate({ integrationProviderId, tenantId: effectiveTenantId }),
  };
};
